import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Slider,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from 'tss-react/mui';
import TuneIcon from '@mui/icons-material/Tune';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import MapIcon from '@mui/icons-material/Map';
import ExploreIcon from '@mui/icons-material/Explore';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlaceIcon from '@mui/icons-material/Place';
import SpeedIcon from '@mui/icons-material/Speed';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RouteIcon from '@mui/icons-material/Route';
import RadarIcon from '@mui/icons-material/Radar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import MapView from '../map/core/MapView';
import MapRoutePath from '../map/MapRoutePath';
import MapRoutePoints from '../map/MapRoutePoints';
import MapPositions from '../map/MapPositions';
import { formatTime } from '../common/util/formatter';
import ReportFilter, { updateReportParams } from '../reports/components/ReportFilter';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useCatch, useCatchCallback } from '../reactHelper';
import MapCamera from '../map/MapCamera';
import MapGeofence from '../map/MapGeofence';
import MapScale from '../map/MapScale';
import BackIcon from '../common/components/BackIcon';
import fetchOrThrow from '../common/util/fetchOrThrow';
import MapOverlay from '../map/overlay/MapOverlay';
import { openUrl } from '../common/util/navigationFallback';
import { buildMapsLinks, reportMapOpenFailure } from '../common/util/mapLinks';
import StatusCard from '../common/components/StatusCard';

const REPLAY_PANEL_WIDTH = 340;
const REPLAY_PANEL_GAP = 12;
const MOBILE_CARD_HEIGHT = 520;
const DESKTOP_CARD_WIDTH = 336;
const DEFAULT_REPLAY_INTERVAL = 500;
const REPLAY_SPEEDS = [250, 500, 1000, 1500];
const EMPTY_TEXT = '—';
const MAX_VISIBLE_ROUTE_POINTS = 2000;


const getReplayRouteColor = (deviceId) => {
  const normalized = Number(deviceId);
  const safe = Number.isFinite(normalized) ? Math.abs(Math.trunc(normalized)) : 0;

  // Golden angle distributes hue with low collision even for hundreds of IDs.
  const hue = Math.round((safe * 137.508) % 360);

  // Add extra variation for large fleets while keeping good map visibility.
  const saturationSteps = [68, 76, 84];
  const lightnessSteps = [42, 50, 58];
  const satIndex = Math.trunc(safe / 360) % saturationSteps.length;
  const lightIndex = Math.trunc(safe / (360 * saturationSteps.length)) % lightnessSteps.length;

  return `hsl(${hue} ${saturationSteps[satIndex]}% ${lightnessSteps[lightIndex]}%)`;
};

const isFiniteNumber = (value) => Number.isFinite(Number(value));

const knotsToKmh = (knots) => {
  if (knots === null || knots === undefined) return null;
  const n = Number(knots);
  if (Number.isNaN(n)) return null;
  return n * 1.852;
};

const safeClipboardWrite = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const hasValidCoordinates = (position) => (
  isFiniteNumber(position?.latitude) && isFiniteNumber(position?.longitude)
);

const getReplayStatus = (position) => {
  const ignition = position?.attributes?.ignition;
  const speedKmh = knotsToKmh(position?.speed);
  const moving = speedKmh != null && speedKmh > 2;

  if (moving) {
    return {
      key: 'MOVING',
      label: `Em movimento (${Math.round(speedKmh)} km/h)`,
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />,
      dotColor: 'success.main',
    };
  }

  if (ignition === false) {
    return {
      key: 'IGNITION_OFF',
      label: 'Ignição desligada',
      color: 'error',
      icon: <PowerSettingsNewIcon fontSize="small" />,
      dotColor: 'error.main',
    };
  }

  if (ignition === true) {
    return {
      key: 'IGNITION_ON_STOPPED',
      label: 'Parado c/ motor ligado',
      color: 'warning',
      icon: <WarningAmberIcon fontSize="small" />,
      dotColor: 'warning.main',
    };
  }

  return {
    key: 'NO_DATA',
    label: 'Sem dados',
    color: 'default',
    icon: <ErrorOutlineIcon fontSize="small" />,
    dotColor: 'text.disabled',
  };
};


const getSpeedToneClass = (speedKmh, ignition) => {
  if (speedKmh == null || speedKmh <= 0) return 'neutral';
  if (speedKmh > 2) return 'moving';
  if (ignition === true) return 'alert';
  return 'neutral';
};

const createSliderMarks = (count) => {
  if (!count) return [];

  if (count <= 20) {
    return Array.from({ length: count }, (_, i) => ({ value: i }));
  }

  const desiredMarks = 10;
  const step = Math.max(1, Math.ceil((count - 1) / desiredMarks));
  const values = new Set([0, count - 1]);

  for (let i = 0; i < count; i += step) {
    values.add(i);
  }

  return Array.from(values)
    .sort((a, b) => a - b)
    .map((value) => ({ value }));
};

const getReplaySpeedLabel = (intervalMs) => {
  if (intervalMs <= 250) return '4x';
  if (intervalMs <= 500) return '2x';
  if (intervalMs <= 1000) return '1x';
  return '0.5x';
};

const useStyles = makeStyles()((theme) => ({
  root: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    background: theme.palette.background.default,
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    zIndex: 3,
    left: 0,
    top: 0,
    margin: theme.spacing(1.5),
    width: REPLAY_PANEL_WIDTH,
    maxHeight: 'calc(100vh - 24px)',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      margin: 0,
      maxHeight: 'none',
    },
  },
  title: {
    flexGrow: 1,
    minWidth: 0,
  },
  toolbar: {
    minHeight: 56,
  },
  slider: {
    width: '100%',
  },
  sliderMobile: {
    '& .MuiSlider-rail, & .MuiSlider-track': {
      height: 8,
      borderRadius: 999,
    },
    '& .MuiSlider-thumb': {
      width: 20,
      height: 20,
    },
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  controlGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  controlProgress: {
    flex: '1 1 120px',
  },
  controlButtons: {
    flex: '0 0 auto',
    '& .MuiIconButton-root': {
      width: 48,
      height: 48,
    },
  },
  controlMeta: {
    flex: '1 1 220px',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    '& > .controlTextCell': {
      minWidth: 0,
    },
  },
  controlTextCell: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
  },
  controlTextCellEnd: {
    justifyContent: 'flex-end',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    background: 'rgba(255,255,255,0.96)',
    gap: theme.spacing(1.25),
    overflow: 'auto',
    overflowX: 'hidden',
    [theme.breakpoints.down('md')]: {
      margin: theme.spacing(1),
      borderRadius: theme.shape.borderRadius * 1.5,
      padding: theme.spacing(1.25),
      gap: theme.spacing(1),
    },
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(1),
    },
  },
  mobileContentHidden: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  mobileMiniPlayerOverlay: {
    position: 'absolute',
    left: theme.spacing(1),
    right: theme.spacing(1),
    top: 68,
    zIndex: 5,
    pointerEvents: 'none',
    display: 'flex',
    justifyContent: 'center',
  },
  mobileMiniPlayer: {
    pointerEvents: 'auto',
    width: '100%',
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: theme.shadows[6],
    padding: theme.spacing(1, 1.25),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  centeredState: {
    minHeight: 140,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  desktopCard: {
    position: 'fixed',
    zIndex: 3,
    top: theme.spacing(1.5),
    left: `calc(${REPLAY_PANEL_WIDTH}px + ${theme.spacing(1.5)} + ${REPLAY_PANEL_GAP}px)`,
    width: DESKTOP_CARD_WIDTH,
    maxWidth: `calc(100vw - ${REPLAY_PANEL_WIDTH}px - ${DESKTOP_CARD_WIDTH}px - 56px)`,
    maxHeight: 'calc(100vh - 24px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  mobileSelectionOverlay: {
    position: 'absolute',
    left: theme.spacing(1),
    right: theme.spacing(1),
    bottom: theme.spacing(1),
    zIndex: 6,
    pointerEvents: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  mobileCollapsedBar: {
    pointerEvents: 'auto',
    width: '100%',
    maxWidth: '100%',
    minHeight: 72,
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: theme.shadows[8],
    background: theme.palette.background.paper,
  },
  mobileSelectionPaper: {
    pointerEvents: 'auto',
    width: '100%',
    maxWidth: '100%',
    height: `min(68vh, ${MOBILE_CARD_HEIGHT}px)`,
    minHeight: 300,
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: theme.shadows[8],
    background: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
  },
  replayCardRoot: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
  },
  replayCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.1, 1.2, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  replayCardHeaderText: {
    minWidth: 0,
    flex: 1,
  },
  replayCardBody: {
    padding: theme.spacing(1.25),
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.1),
    minHeight: 0,
  },
  quickActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  quickBtn: {
    textTransform: 'none',
    borderRadius: 10,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1),
  },
  infoBlock: {
    minWidth: 0,
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  speedNeutral: { color: theme.palette.text.secondary },
  speedMoving: { color: theme.palette.success.main, fontWeight: 700 },
  speedAlert: { color: theme.palette.warning.main, fontWeight: 700 },
  table: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.75),
      verticalAlign: 'top',
    },
    '& .MuiTableCell-root:first-of-type': {
      width: '38%',
      paddingRight: theme.spacing(1),
    },
  },
  miniBarRoot: {
    display: 'flex',
    flexDirection: 'column',
  },
  miniBarHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.1, 1.25, 0.9),
  },
  miniBarBody: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto auto',
    gap: theme.spacing(1),
    alignItems: 'center',
    padding: theme.spacing(0.9, 1.25, 1.1),
  },
  miniBarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
  },
  showMoreLink: {
    alignSelf: 'flex-start',
    cursor: 'pointer',
  },
  metaBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  statText: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      textOverflow: 'clip',
    },
  },
  keyboardHint: {
    maxWidth: 180,
  },
  primaryAction: { flexGrow: 1 },
  mobileActionRow: {
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
}));

const ReplayMiniBar = ({
  deviceName,
  position,
  onExpand,
  onClose,
}) => {
  const { classes } = useStyles();
  const status = getReplayStatus(position);
  const speedKmh = knotsToKmh(position?.speed);

  return (
    <div className={classes.miniBarRoot}>
      <div className={classes.miniBarHeader}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: status.dotColor,
            flexShrink: 0,
          }}
        />

        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, flex: 1 }}>
          {deviceName || `#${position?.deviceId ?? ''}`}
        </Typography>

        <div className={classes.miniBarActions}>
          <IconButton size="small" onClick={onExpand} aria-label="Expandir detalhes">
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>

          <IconButton size="small" onClick={onClose} aria-label="Fechar detalhes">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <Divider />

      <div className={classes.miniBarBody}>
        <Typography variant="body2" noWrap className={classes.muted}>
          {status.label}
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
          {speedKmh == null ? '— km/h' : `${Math.round(speedKmh)} km/h`}
        </Typography>

        <Typography variant="caption" className={classes.muted} sx={{ whiteSpace: 'nowrap' }}>
          {position?.fixTime ? formatTime(position.fixTime, 'seconds') : EMPTY_TEXT}
        </Typography>
      </div>
    </div>
  );
};

const ReplayInfoCard = ({
  position,
  onClose,
  onCollapse,
  mobileExpanded = false,
}) => {
  return (
    <StatusCard
      deviceId={position?.deviceId}
      position={position}
      onClose={onClose}
      onCollapse={onCollapse}
      docked
      desktopPadding={0}
      mobileExpanded={mobileExpanded}
    />
  );
};

const ReplayPage = () => {
  const t = useTranslation();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();

  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const autoLoadKeyRef = useRef('');
  const lastRequestedKeyRef = useRef('');
  const controlsRef = useRef(null);

  const defaultDeviceId = useSelector((state) => state.devices.selectedId);
  const devices = useSelector((state) => state.devices.items);
  const groups = useSelector((state) => state.groups.items);

  const [deviceReplayState, setDeviceReplayState] = useState({});
  const [activeDeviceId, setActiveDeviceId] = useState(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState(defaultDeviceId ? [defaultDeviceId] : []);
  const [showCard, setShowCard] = useState(false);
  const [mobileCardExpanded, setMobileCardExpanded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [mapsAnchorEl, setMapsAnchorEl] = useState(null);
  const [speedAnchorEl, setSpeedAnchorEl] = useState(null);
  const [deviceMenuAnchorEl, setDeviceMenuAnchorEl] = useState(null);
  const [replayInterval, setReplayInterval] = useState(DEFAULT_REPLAY_INTERVAL);
  const [mobileControlsMinimized, setMobileControlsMinimized] = useState(false);
  const [controlsWidth, setControlsWidth] = useState(0);
  const [failedMapUrl, setFailedMapUrl] = useState('');

  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const urlDeviceIds = searchParams.getAll('deviceId').filter(Boolean);
  const urlGroupIds = searchParams.getAll('groupId').filter(Boolean);
  const urlDeviceId = urlDeviceIds[0] || null;

  const resolveDeviceIds = useCallback(({ selectedDeviceIds = [], selectedGroupIds = [] }) => {
    const result = new Set((selectedDeviceIds || []).filter(Boolean).map(Number));
    const groupSet = new Set((selectedGroupIds || []).filter(Boolean).map(Number));
    if (!groupSet.size) return [...result];

    const stack = [...groupSet];
    while (stack.length) {
      const current = stack.pop();
      Object.values(groups).forEach((group) => {
        if (group?.groupId === current && !groupSet.has(group.id)) {
          groupSet.add(group.id);
          stack.push(group.id);
        }
      });
    }

    Object.values(devices).forEach((device) => {
      if (groupSet.has(device.groupId)) result.add(device.id);
    });

    return [...result];
  }, [devices, groups]);

  const hasFilters = Boolean(from && to);
  const hasUrlReplayParams = Boolean((urlDeviceIds.length || urlGroupIds.length) && from && to);
  const activePositions = deviceReplayState[activeDeviceId]?.positions || [];
  const activeIndex = deviceReplayState[activeDeviceId]?.index || 0;
  const hasData = Object.values(deviceReplayState).some((item) => item?.positions?.length);
  const loaded = Boolean(hasFilters && !loading && hasData);

  const selectedPosition = useMemo(
    () => (activeIndex >= 0 && activeIndex < activePositions.length ? activePositions[activeIndex] : null),
    [activeIndex, activePositions],
  );

  const deviceName = useSelector((state) => {
    const effectiveDeviceId = selectedPosition?.deviceId || selectedDeviceIds[0] || urlDeviceId;
    if (!effectiveDeviceId) return null;
    const device = state.devices.items[effectiveDeviceId];
    return device?.name || null;
  });

  const mapsLinks = useMemo(
    () => buildMapsLinks(selectedPosition),
    [selectedPosition],
  );

  const sliderMarks = useMemo(
    () => createSliderMarks(activePositions.length),
    [activePositions.length],
  );
  const routePoints = useMemo(() => {
    if (activePositions.length <= MAX_VISIBLE_ROUTE_POINTS) {
      return activePositions.map((position, sourceIndex) => ({ position, sourceIndex }));
    }

    const step = Math.ceil(activePositions.length / MAX_VISIBLE_ROUTE_POINTS);
    const sampled = [];
    activePositions.forEach((position, sourceIndex) => {
      if (sourceIndex % step === 0) {
        sampled.push({ position, sourceIndex });
      }
    });

    const lastIndex = activePositions.length - 1;
    const hasLastPoint = sampled[sampled.length - 1]?.sourceIndex === lastIndex;
    if (!hasLastPoint) {
      sampled.push({ position: activePositions[lastIndex], sourceIndex: lastIndex });
    }
    return sampled;
  }, [activePositions]);

  const replayProgressLabel = useMemo(() => {
    if (!hasData) return '0 de 0';
    return `${activeIndex + 1} de ${activePositions.length}`;
  }, [hasData, activeIndex, activePositions.length]);

  const getDeviceDisplayName = useCallback((deviceId) => {
    const normalizedId = Number(deviceId);
    return devices[normalizedId]?.name || `#${deviceId}`;
  }, [devices]);
  const showKeyboardHint = !isSmallScreen && controlsWidth >= 680;
  const activeReplayDevices = useMemo(
    () => Object.entries(deviceReplayState),
    [deviceReplayState],
  );

  const closeMenus = useCallback(() => {
    setActionsAnchorEl(null);
    setMapsAnchorEl(null);
    setSpeedAnchorEl(null);
  }, []);

  const stopReplay = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    setPlaying(false);
  }, []);

  const closeCard = useCallback(() => {
    setShowCard(false);
    setMobileCardExpanded(false);
    closeMenus();
  }, [closeMenus]);

  const openCard = useCallback(() => {
    setShowCard(true);
    setMobileCardExpanded(false);
  }, []);

  const openExternalLink = useCallback((url, provider) => {
    if (!url) return;
    const result = openUrl(url, { preferSameTab: false });
    if (!result.openedInNewTab) {
      setFailedMapUrl(url);
      reportMapOpenFailure({
        source: 'replay_page',
        provider,
        url,
        deviceId: selectedPosition?.deviceId,
        positionId: selectedPosition?.id,
      });
    }
  }, [selectedPosition?.deviceId, selectedPosition?.id]);

  const findPositionIndexById = useCallback((positionId) => {
    if (!positionId) return -1;
    return activePositions.findIndex((p) => p.id === positionId);
  }, [activePositions]);

  const setSelectedIndexSafe = useCallback((nextIndex) => {
    if (!activeDeviceId) return;
    setDeviceReplayState((current) => {
      const currentDevice = current[activeDeviceId];
      if (!currentDevice?.positions?.length) return current;
      const safeIndex = Math.max(0, Math.min(nextIndex, currentDevice.positions.length - 1));
      return { ...current, [activeDeviceId]: { ...currentDevice, index: safeIndex } };
    });
  }, [activeDeviceId]);

  const loadReplay = useCallback(async ({
    deviceIds,
    from: nextFrom,
    to: nextTo,
    preserveSearchParams = false,
  }) => {
    const normalizedDeviceIds = Array.isArray(deviceIds)
      ? deviceIds.map((id) => Number(id)).filter(Boolean)
      : [];

    if (!normalizedDeviceIds.length) {
      setErrorMessage('Nenhum dispositivo selecionado');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setSelectedDeviceIds(normalizedDeviceIds);
    setDeviceReplayState({});
    setActiveDeviceId(normalizedDeviceIds[0] || null);
    closeCard();
    stopReplay();

    if (!preserveSearchParams) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.delete('deviceId');
        normalizedDeviceIds.forEach((id) => params.append('deviceId', String(id)));
        params.set('from', nextFrom);
        params.set('to', nextTo);
        return params;
      });
    }

    try {
      const responses = await Promise.all(normalizedDeviceIds.map(async (deviceId) => {
        const query = new URLSearchParams({
          deviceId: String(deviceId),
          from: nextFrom,
          to: nextTo,
        });
        const response = await fetchOrThrow(`/api/positions?${query.toString()}`);
        return response.json();
      }));

      if (!mountedRef.current) return;

      const nextReplayState = {};
      normalizedDeviceIds.forEach((deviceId, responseIndex) => {
        const positions = Array.isArray(responses[responseIndex]) ? responses[responseIndex] : [];
        nextReplayState[deviceId] = {
          positions: positions.sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime()),
          index: 0,
        };
      });
      setDeviceReplayState(nextReplayState);
      if (!activeDeviceId || !nextReplayState[activeDeviceId]) setActiveDeviceId(normalizedDeviceIds[0] || null);

      if (!Object.values(nextReplayState).some((item) => item.positions.length)) {
        setErrorMessage(t('sharedNoData'));
      }
    } catch (error) {
      if (!mountedRef.current) return;
      setDeviceReplayState({});
      setActiveDeviceId(null);
      setErrorMessage(error?.message || 'Falha ao carregar replay');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [closeCard, stopReplay, setSearchParams, t]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (desktop) {
      setMobileCardExpanded(false);
    }
  }, [desktop]);

  useEffect(() => {
    const node = controlsRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      setControlsWidth(node.clientWidth || 0);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);

    return () => observer.disconnect();
  }, [loaded, desktop]);

  useEffect(() => {
    if (!from && !to && !urlDeviceId && !urlGroupIds.length) {
      setDeviceReplayState({});
      setActiveDeviceId(defaultDeviceId || null);
      setSelectedDeviceIds(defaultDeviceId ? [defaultDeviceId] : []);
      setErrorMessage('');
      closeCard();
      stopReplay();
      autoLoadKeyRef.current = '';
      lastRequestedKeyRef.current = '';
    }
  }, [from, to, urlDeviceId, urlGroupIds.length, defaultDeviceId, closeCard, stopReplay]);

  useEffect(() => {
  }, [deviceReplayState]);

  useEffect(() => {
    if (!playing || activePositions.length === 0 || !activeDeviceId) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      return undefined;
    }

    timerRef.current = setInterval(() => {
      setDeviceReplayState((current) => {
        const currentDevice = current[activeDeviceId];
        if (!currentDevice) return current;
        const currentIndex = currentDevice.index || 0;
        if (currentIndex >= currentDevice.positions.length - 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          setPlaying(false);
          return current;
        }
        return {
          ...current,
          [activeDeviceId]: { ...currentDevice, index: currentIndex + 1 },
        };
      });
    }, replayInterval);

    return () => {
      clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [playing, activePositions.length, activeDeviceId, replayInterval]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!activePositions.length) return;

      const targetTag = event.target?.tagName?.toLowerCase();
      const isTyping = ['input', 'textarea', 'select'].includes(targetTag);
      if (isTyping) return;

      if (event.code === 'Space') {
        event.preventDefault();
        setPlaying((current) => {
          if (!current && activeIndex >= activePositions.length - 1) {
            setSelectedIndexSafe(0);
          }
          return !current;
        });
      }

      if (event.code === 'ArrowLeft') {
        event.preventDefault();
        stopReplay();
        setSelectedIndexSafe(activeIndex - 1);
      }

      if (event.code === 'ArrowRight') {
        event.preventDefault();
        stopReplay();
        setSelectedIndexSafe(activeIndex + 1);
      }

      if (event.code === 'Escape') {
        closeCard();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activePositions.length, activeIndex, stopReplay, setSelectedIndexSafe, closeCard]);

  useEffect(() => {
    if (!hasUrlReplayParams) return;

    const autoloadKey = `${urlDeviceIds.join(',')}_${urlGroupIds.join(',')}_${from}_${to}`;
    if (autoLoadKeyRef.current === autoloadKey) return;

    autoLoadKeyRef.current = autoloadKey;

    loadReplay({
      deviceIds: resolveDeviceIds({ selectedDeviceIds: urlDeviceIds, selectedGroupIds: urlGroupIds }),
      from,
      to,
      preserveSearchParams: true,
    });
  }, [hasUrlReplayParams, urlDeviceIds, urlGroupIds, from, to, loadReplay, resolveDeviceIds]);

  const onPointClick = useCallback((_, nextIndex) => {
    stopReplay();
    setSelectedIndexSafe(nextIndex);
    openCard();
    closeMenus();
  }, [stopReplay, setSelectedIndexSafe, openCard, closeMenus]);

  const onMarkerClick = useCallback((positionId) => {
    if (!positionId) {
      closeCard();
      return;
    }

    const nextIndex = findPositionIndexById(positionId);
    if (nextIndex === -1) return;

    stopReplay();
    setSelectedIndexSafe(nextIndex);
    openCard();
    closeMenus();
  }, [findPositionIndexById, stopReplay, setSelectedIndexSafe, openCard, closeMenus, closeCard]);

  const onShow = useCatch(async ({ deviceIds, groupIds, from: nextFrom, to: nextTo }) => {
    const validDeviceIds = resolveDeviceIds({ selectedDeviceIds: deviceIds, selectedGroupIds: groupIds });
    const requestKey = `${validDeviceIds.join(',')}_${nextFrom || ''}_${nextTo || ''}`;

    if (lastRequestedKeyRef.current === requestKey) {
      return;
    }

    lastRequestedKeyRef.current = requestKey;
    await loadReplay({ deviceIds: validDeviceIds, from: nextFrom, to: nextTo });
  });

  const handleDownload = useCallback(() => {
    const effectiveDeviceId = selectedPosition?.deviceId || selectedDeviceIds[0] || urlDeviceId;
    if (!effectiveDeviceId || !from || !to) return;

    const query = new URLSearchParams({
      deviceId: String(effectiveDeviceId),
      from,
      to,
    });

    window.location.assign(`/api/positions/kml?${query.toString()}`);
  }, [selectedPosition?.deviceId, selectedDeviceIds, urlDeviceId, from, to]);

  const handleExpandMobileCard = useCallback(() => {
    setMobileCardExpanded(true);
  }, []);

  const handleCollapseMobileCard = useCallback(() => {
    setMobileCardExpanded(false);
  }, []);

  const handlePrevious = useCallback(() => {
    stopReplay();
    setSelectedIndexSafe(activeIndex - 1);
  }, [activeIndex, stopReplay, setSelectedIndexSafe]);

  const handleNext = useCallback(() => {
    stopReplay();
    setSelectedIndexSafe(activeIndex + 1);
  }, [activeIndex, stopReplay, setSelectedIndexSafe]);

  const handleTogglePlay = useCallback(() => {
    if (!activePositions.length) return;

    setPlaying((current) => {
      if (!current && activeIndex >= activePositions.length - 1) {
        setSelectedIndexSafe(0);
      }
      return !current;
    });
  }, [activeIndex, activePositions.length]);

  const handleSliderChange = useCallback((_, nextValue) => {
    if (typeof nextValue !== 'number') return;
    stopReplay();
    setSelectedIndexSafe(nextValue);
  }, [stopReplay, setSelectedIndexSafe]);

  const handleCopyCoords = useCallback(async () => {
    if (!hasValidCoordinates(selectedPosition)) {
      setToast('Coordenadas indisponíveis');
      return;
    }

    const text = `${selectedPosition.latitude},${selectedPosition.longitude}`;
    const ok = await safeClipboardWrite(text);
    setToast(ok ? 'Coordenadas copiadas' : 'Não foi possível copiar as coordenadas');
  }, [selectedPosition]);

  const handleCopyAddress = useCallback(async () => {
    if (!selectedPosition?.address) {
      setToast('Endereço indisponível');
      return;
    }

    const ok = await safeClipboardWrite(selectedPosition.address);
    setToast(ok ? 'Endereço copiado' : 'Não foi possível copiar o endereço');
  }, [selectedPosition]);

  const handleCreateGeofence = useCatchCallback(async () => {
    if (!hasValidCoordinates(selectedPosition)) {
      setToast('Coordenadas inválidas para criar geocerca');
      return;
    }

    closeMenus();

    const newItem = {
      name: t('sharedGeofence'),
      area: `CIRCLE (${selectedPosition.latitude} ${selectedPosition.longitude}, 50)`,
    };

    const response = await fetchOrThrow('/api/geofences', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newItem),
    });
    const item = await response.json();

    if (selectedPosition.deviceId) {
      await fetchOrThrow('/api/permissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceId: selectedPosition.deviceId,
          geofenceId: item.id,
        }),
      });
    }

    navigate(`/settings/geofence/${item.id}`);
  }, [selectedPosition, t, navigate, closeMenus]);

  const handleOpenPositionDetails = useCallback(() => {
    if (!selectedPosition?.id) return;
    navigate(`/position/${selectedPosition.id}`);
  }, [navigate, selectedPosition]);

  const handleOpenMapsMenuFromActions = useCallback(() => {
    setMapsAnchorEl(actionsAnchorEl);
    setActionsAnchorEl(null);
  }, [actionsAnchorEl]);

  const handleChangeSpeed = useCallback((interval) => {
    setReplayInterval(interval);
    setSpeedAnchorEl(null);
  }, []);

  const selectedSpeedKmh = knotsToKmh(selectedPosition?.speed);
  const selectedSpeedTone = getSpeedToneClass(selectedSpeedKmh, selectedPosition?.attributes?.ignition);

  return (
    <div className={classes.root}>
      <MapView>
        <MapOverlay />
        <MapGeofence />
        {Object.entries(deviceReplayState).map(([deviceId, replay]) => (
          <MapRoutePath
            key={deviceId}
            positions={replay.positions || []}
            routeColor={getReplayRouteColor(deviceId)}
          />
        ))}
        <MapRoutePoints
          positions={routePoints.map((item) => item.position)}
          getPositionIndex={(_, routePointIndex) => routePoints[routePointIndex]?.sourceIndex}
          onClick={onPointClick}
          showSpeedControl
        />
        {selectedPosition && (
          <MapPositions
            positions={[selectedPosition]}
            onMarkerClick={onMarkerClick}
            titleField="fixTime"
          />
        )}
      </MapView>

      <MapScale />
      <MapCamera positions={activePositions} />

      <div className={classes.sidebar}>
        <Paper elevation={3} square>
          <Toolbar className={classes.toolbar}>
            <IconButton edge="start" sx={{ mr: 2 }} onClick={() => navigate(-1)} aria-label="Voltar">
              <BackIcon />
            </IconButton>

            <Typography variant="h6" className={classes.title} noWrap>
              {t('reportReplay')}
            </Typography>

            {loaded && (
              <>
                {!desktop && (
                  <Tooltip title={mobileControlsMinimized ? 'Expandir controles' : 'Minimizar controles'}>
                    <IconButton onClick={() => setMobileControlsMinimized((prev) => !prev)}>
                      {mobileControlsMinimized ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Baixar KML">
                  <span>
                    <IconButton onClick={handleDownload} disabled={!hasData}>
                      <DownloadIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Filtros">
                  <IconButton
                    edge="end"
                    onClick={() => updateReportParams(searchParams, setSearchParams, 'ignore', [])}
                  >
                    <TuneIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Toolbar>
        </Paper>

        <Paper className={`${classes.content} ${!desktop && mobileControlsMinimized ? classes.mobileContentHidden : ''}`} square>
          {loaded ? (
            <>
              <div className={classes.metaBar}>
                <Box minWidth={0}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap align="center">
                    {deviceName || `#${selectedPosition?.deviceId || selectedDeviceIds[0] || urlDeviceId || ''}`}
                  </Typography>
                </Box>

              <Chip
                  size="small"
                  variant="outlined"
                  label={`Velocidade ${getReplaySpeedLabel(replayInterval)}`}
                  onClick={(e) => setSpeedAnchorEl(e.currentTarget)}
                />
              </div>
              {activeReplayDevices.length > 1 && (
                isSmallScreen ? (
                  <Box
                    display="flex"
                    gap={1}
                    mb={1}
                    alignItems="center"
                    sx={{ overflowX: 'auto', pb: 0.25 }}
                  >
                    {activeReplayDevices
                      .filter(([deviceId]) => Number(deviceId) === Number(activeDeviceId))
                      .map(([deviceId, replay]) => (
                        <Chip
                          key={deviceId}
                          color="primary"
                          variant="filled"
                          sx={{ borderColor: getReplayRouteColor(deviceId), flexShrink: 0 }}
                          label={`${getDeviceDisplayName(deviceId)} (${(replay.index || 0) + 1}/${replay.positions.length || 0})`}
                        />
                      ))}

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => setDeviceMenuAnchorEl(e.currentTarget)}
                      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      Veículos ({activeReplayDevices.length})
                    </Button>
                  </Box>
                ) : (
                  <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                    {activeReplayDevices.map(([deviceId, replay]) => (
                      <Chip
                        key={deviceId}
                        color={Number(deviceId) === Number(activeDeviceId) ? 'primary' : 'default'}
                        variant={Number(deviceId) === Number(activeDeviceId) ? 'filled' : 'outlined'}
                        sx={{ borderColor: getReplayRouteColor(deviceId) }}
                        label={`${getDeviceDisplayName(deviceId)} (${(replay.index || 0) + 1}/${replay.positions.length || 0})`}
                        onClick={() => {
                          stopReplay();
                          setActiveDeviceId(Number(deviceId));
                        }}
                      />
                    ))}
                  </Box>
                )
              )}

              <Slider
                className={`${classes.slider} ${!desktop ? classes.sliderMobile : ''}`}
                max={Math.max(activePositions.length - 1, 0)}
                min={0}
                step={1}
                marks={sliderMarks}
                value={Math.min(activeIndex, Math.max(activePositions.length - 1, 0))}
                onChange={handleSliderChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => {
                  const pos = activePositions[value];
                  return pos?.fixTime ? formatTime(pos.fixTime, 'seconds') : `${value + 1}`;
                }}
                aria-label="Timeline do replay"
              />

              <div className={classes.controls} ref={controlsRef}>
                <div className={`${classes.controlGroup} ${classes.controlProgress}`}>
                  <Box className={classes.controlTextCell}>
                    <Typography variant="body2" className={classes.statText} title={replayProgressLabel}>
                      {replayProgressLabel}
                    </Typography>
                  </Box>
                </div>

                <div className={`${classes.controlGroup} ${classes.controlButtons}`}>
                  <Tooltip title="Anterior">
                    <span>
                      <IconButton
                        onClick={handlePrevious}
                        disabled={playing || activeIndex <= 0}
                      >
                        <FastRewindIcon />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title={playing ? 'Pausar' : 'Reproduzir'}>
                    <span>
                      <IconButton
                        onClick={handleTogglePlay}
                        disabled={!activePositions.length || activeIndex >= activePositions.length - 1}
                      >
                        {playing ? <PauseIcon /> : <PlayArrowIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Próximo">
                    <span>
                      <IconButton
                        onClick={handleNext}
                        disabled={playing || activeIndex >= activePositions.length - 1}
                      >
                        <FastForwardIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>

                <div className={`${classes.controlGroup} ${classes.controlMeta}`}>
                  <Box className={`controlTextCell ${classes.controlTextCell}`}>
                    <Typography
                      variant="body2"
                      className={classes.statText}
                      title={selectedPosition?.fixTime ? formatTime(selectedPosition.fixTime, 'seconds') : EMPTY_TEXT}
                    >
                      {selectedPosition?.fixTime
                        ? formatTime(selectedPosition.fixTime, 'seconds')
                        : EMPTY_TEXT}
                    </Typography>
                  </Box>

                  <Box className={`controlTextCell ${classes.controlTextCell}`}>
                    <Typography
                      variant="body2"
                      className={`${classes.statText} ${selectedSpeedTone === 'moving' ? classes.speedMoving : selectedSpeedTone === 'alert' ? classes.speedAlert : classes.speedNeutral}`}
                    >
                      {selectedPosition
                        ? `${Math.round(knotsToKmh(selectedPosition.speed) || 0)} km/h`
                        : '— km/h'}
                    </Typography>
                  </Box>

                  {showKeyboardHint && (
                    <Box className={`controlTextCell ${classes.controlTextCell} ${classes.controlTextCellEnd}`}>
                      <Typography
                        variant="body2"
                        className={`${classes.statText} ${classes.muted} ${classes.keyboardHint}`}
                      >
                        Espaço: play/pause
                      </Typography>
                    </Box>
                  )}
                </div>
              </div>

              <Divider />

              <Box className={classes.mobileActionRow} sx={!desktop ? { flexWrap: 'nowrap' } : { flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="contained"
                  className={classes.primaryAction}
                  onClick={() => {
                    if (selectedPosition) openCard();
                  }}
                  disabled={!selectedPosition}
                >
                  Ver detalhes
                </Button>

                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    stopReplay();
                    setSelectedIndexSafe(0);
                  }}
                  disabled={!activePositions.length}
                >
                  Voltar ao início
                </Button>
              </Box>
            </>
          ) : (
            <>
              {loading ? (
                <div className={classes.centeredState}>
                  <CircularProgress size={28} />
                  <Typography variant="body2" className={classes.muted}>
                    Carregando replay...
                  </Typography>
                </div>
              ) : (
                <>
                  {errorMessage && (
                    <Alert severity="info">
                      {errorMessage}
                    </Alert>
                  )}

                  <div style={{ display: 'block' }}>
                    <ReportFilter
                      onShow={onShow}
                      deviceType="multiple"
                      loading={loading}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </Paper>
      </div>

      {!desktop && loaded && mobileControlsMinimized && (
        <Box className={classes.mobileMiniPlayerOverlay}>
          <Paper elevation={6} className={classes.mobileMiniPlayer}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                {deviceName || `#${selectedPosition?.deviceId || ''}`}
              </Typography>
              <Typography variant="caption" className={classes.muted}>
                {replayProgressLabel}
              </Typography>
            </Box>

            <Slider
              className={classes.slider}
              max={Math.max(activePositions.length - 1, 0)}
              min={0}
              step={1}
              marks={[]}
              value={Math.min(activeIndex, Math.max(activePositions.length - 1, 0))}
              onChange={handleSliderChange}
              aria-label="Timeline compacta do replay"
            />

            <Box display="flex" alignItems="center" justifyContent="space-between">
              <IconButton onClick={handlePrevious} disabled={playing || activeIndex <= 0}>
                <FastRewindIcon />
              </IconButton>
              <IconButton
                onClick={handleTogglePlay}
                disabled={!activePositions.length || activeIndex >= activePositions.length - 1}
              >
                {playing ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <IconButton onClick={handleNext} disabled={playing || activeIndex >= activePositions.length - 1}>
                <FastForwardIcon />
              </IconButton>
              <Button size="small" variant="outlined" onClick={() => setMobileControlsMinimized(false)}>
                Abrir painel
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {showCard && selectedPosition && desktop && (
        <Paper elevation={3} className={classes.desktopCard}>
          <ReplayInfoCard
            deviceName={deviceName}
            position={selectedPosition}
            onClose={closeCard}
            onOpenActionsMenu={(e) => setActionsAnchorEl(e.currentTarget)}
            onOpenMapsMenu={(e) => setMapsAnchorEl(e.currentTarget)}
            onCopyCoords={handleCopyCoords}
            onCopyAddress={handleCopyAddress}
            onCreateGeofence={handleCreateGeofence}
            onOpenPositionDetails={handleOpenPositionDetails}
          />
        </Paper>
      )}

      {showCard && selectedPosition && !desktop && !mobileCardExpanded && (
        <Box className={classes.mobileSelectionOverlay}>
          <Paper elevation={6} className={classes.mobileCollapsedBar}>
            <ReplayMiniBar
              deviceName={deviceName}
              position={selectedPosition}
              onExpand={handleExpandMobileCard}
              onClose={closeCard}
            />
          </Paper>
        </Box>
      )}

      {showCard && selectedPosition && !desktop && mobileCardExpanded && (
        <Box className={classes.mobileSelectionOverlay}>
          <Paper elevation={6} className={classes.mobileSelectionPaper}>
            <ReplayInfoCard
              deviceName={deviceName}
              position={selectedPosition}
              onClose={closeCard}
              onCollapse={handleCollapseMobileCard}
              mobileExpanded
              onOpenActionsMenu={(e) => setActionsAnchorEl(e.currentTarget)}
              onOpenMapsMenu={(e) => setMapsAnchorEl(e.currentTarget)}
              onCopyCoords={handleCopyCoords}
              onCopyAddress={handleCopyAddress}
              onCreateGeofence={handleCreateGeofence}
              onOpenPositionDetails={handleOpenPositionDetails}
            />
          </Paper>
        </Box>
      )}

      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={() => setActionsAnchorEl(null)}
      >
        <MenuItem
          onClick={handleOpenMapsMenuFromActions}
          disabled={!mapsLinks}
        >
          <ListItemIcon>
            <MapIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Abrir em mapas"
            secondary="Google Maps, Street View, Apple Maps e Waze"
          />
        </MenuItem>

        <MenuItem
          onClick={async () => {
            setActionsAnchorEl(null);
            await handleCopyCoords();
          }}
          disabled={!hasValidCoordinates(selectedPosition)}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Copiar coordenadas"
            secondary="Latitude e longitude"
          />
        </MenuItem>

        <MenuItem
          onClick={async () => {
            setActionsAnchorEl(null);
            await handleCopyAddress();
          }}
          disabled={!selectedPosition?.address}
        >
          <ListItemIcon>
            <PlaceIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Copiar endereço"
            secondary="Copiar endereço do ponto histórico"
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            setActionsAnchorEl(null);
            handleCreateGeofence();
          }}
          disabled={!hasValidCoordinates(selectedPosition)}
        >
          <ListItemIcon>
            <RadarIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Criar geocerca"
            secondary="Criar geocerca no ponto selecionado"
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            setActionsAnchorEl(null);
            handleOpenPositionDetails();
          }}
          disabled={!selectedPosition?.id}
        >
          <ListItemIcon>
            <RouteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Mostrar mais"
            secondary="Abrir detalhes completos da posição"
          />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={mapsAnchorEl}
        open={Boolean(mapsAnchorEl)}
        onClose={() => setMapsAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setMapsAnchorEl(null);
            openExternalLink(mapsLinks?.googleMaps, 'google_maps');
          }}
          disabled={!mapsLinks?.googleMaps}
        >
          <ListItemIcon>
            <MapIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Google Maps"
            secondary="Abrir localização no Google Maps"
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            setMapsAnchorEl(null);
            openExternalLink(mapsLinks?.streetView, 'street_view');
          }}
          disabled={!mapsLinks?.streetView}
        >
          <ListItemIcon>
            <ExploreIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Street View"
            secondary="Abrir visão da rua"
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            setMapsAnchorEl(null);
            openExternalLink(mapsLinks?.appleMaps, 'apple_maps');
          }}
          disabled={!mapsLinks?.appleMaps}
        >
          <ListItemIcon>
            <PlaceIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Apple Maps"
            secondary="Abrir localização no Apple Maps"
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            setMapsAnchorEl(null);
            openExternalLink(mapsLinks?.waze, 'waze');
          }}
          disabled={!mapsLinks?.waze}
        >
          <ListItemIcon>
            <MyLocationIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Waze"
            secondary="Navegar até o ponto"
          />
        </MenuItem>

        <MenuItem
          onClick={async () => {
            setMapsAnchorEl(null);
            await handleCopyCoords();
          }}
          disabled={!hasValidCoordinates(selectedPosition)}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Copiar coordenadas"
            secondary="Latitude e longitude"
          />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={deviceMenuAnchorEl}
        open={Boolean(deviceMenuAnchorEl)}
        onClose={() => setDeviceMenuAnchorEl(null)}
      >
        {activeReplayDevices.map(([deviceId, replay]) => (
          <MenuItem
            key={deviceId}
            selected={Number(deviceId) === Number(activeDeviceId)}
            onClick={() => {
              stopReplay();
              setActiveDeviceId(Number(deviceId));
              setDeviceMenuAnchorEl(null);
            }}
          >
            {getDeviceDisplayName(deviceId)}
            {' '}
            ({(replay.index || 0) + 1}/{replay.positions.length || 0})
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={speedAnchorEl}
        open={Boolean(speedAnchorEl)}
        onClose={() => setSpeedAnchorEl(null)}
      >
        {REPLAY_SPEEDS.map((interval) => (
          <MenuItem
            key={interval}
            selected={interval === replayInterval}
            onClick={() => handleChangeSpeed(interval)}
          >
            <ListItemIcon>
              <SpeedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={`Velocidade ${getReplaySpeedLabel(interval)}`}
              secondary={`${interval} ms por passo`}
            />
          </MenuItem>
        ))}
      </Menu>

      <Dialog
        open={Boolean(failedMapUrl)}
        onClose={() => setFailedMapUrl('')}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Não foi possível abrir o app de mapas</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Abra o link manualmente:
          </Typography>
          <Link href={failedMapUrl} target="_blank" rel="noopener noreferrer">
            {failedMapUrl}
          </Link>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyCoords}>Copiar coordenadas</Button>
          <Button
            onClick={async () => {
              const ok = await safeClipboardWrite(failedMapUrl);
              setToast(ok ? 'URL copiada' : 'Não foi possível copiar a URL');
            }}
          >
            Copiar URL
          </Button>
          <Button onClick={() => setFailedMapUrl('')}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={1800}
        message={toast}
        onClose={() => setToast('')}
      />
    </div>
  );
};

export default ReplayPage;
